// src/modules/purityAnalyzer/pages/CalibrationScreen.jsx
// Copper Calibration — enter copper air/diesel weights, see the computed
// diesel density and temperature index live, then record (and optionally
// activate) the calibration. Every calibration is appended, never overwritten.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, Form, InputNumber, Select, Input, Button, Row, Col, Typography,
  Space, message, Checkbox, Statistic, Tag, Alert,
} from 'antd';
import {
  ExperimentOutlined, ThunderboltOutlined, SaveOutlined, FireOutlined,
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { createCalibration, fetchMachines } from '../api';
import { usePermissions } from '../../../hooks/usePermissions';
import { FLUID_CONDITION_META } from '../constants/purityConstants';

const { Title, Text } = Typography;

// Live client-side preview mirror of the backend Density/Temperature engines
// (server remains authoritative on save).
function previewCalibration(copperAir, copperDiesel, machine) {
  if (!copperAir || !copperDiesel || copperDiesel >= copperAir) return null;
  const referenceDensity = machine?.referenceMetal?.density ?? 8.96;
  const nominal = machine?.immersionFluid?.referenceDensity ?? 0.832;
  const tolerance = machine?.immersionFluid?.driftTolerance ?? 0.015;
  const loss = copperAir - copperDiesel;
  const dieselDensity = referenceDensity * (loss / copperAir);
  const drift = dieselDensity - nominal;
  const temperatureIndex = (drift / nominal) * 100;
  let condition = 'STABLE';
  if (Math.abs(drift) > tolerance) condition = 'CHECK_FLUID';
  else if (drift > tolerance / 2) condition = 'COLD';
  else if (drift < -tolerance / 2) condition = 'WARM';
  return {
    loss: Number(loss.toFixed(4)),
    dieselDensity: Number(dieselDensity.toFixed(5)),
    temperatureIndex: Number(temperatureIndex.toFixed(3)),
    condition,
    referenceDensity,
  };
}

export default function CalibrationScreen() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canActivate = can('purityAnalyzer', 'activateCalibration');

  const [machines, setMachines] = useState([]);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchMachines({ status: 'ACTIVE' })
      .then((res) => setMachines(res?.data?.data || []))
      .catch(() => setMachines([]));
  }, []);

  const machine = useMemo(
    () => machines.find((m) => m._id === values.machineId) || machines[0],
    [machines, values.machineId]
  );
  const preview = useMemo(
    () => previewCalibration(values.copperWeightAir, values.copperWeightDiesel, machine),
    [values, machine]
  );
  const fluid = preview ? (FLUID_CONDITION_META[preview.condition] || FLUID_CONDITION_META.STABLE) : null;

  const onSave = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const res = await createCalibration({
        copperWeightAir: v.copperWeightAir,
        copperWeightDiesel: v.copperWeightDiesel,
        machineId: v.machineId,
        notes: v.notes,
        activate: canActivate && v.activate === true,
      });
      const cal = res?.data?.data;
      message.success(
        `Calibration ${cal?.calibrationNumber} recorded${cal?.status === 'ACTIVE' ? ' and ACTIVATED' : ''}`
      );
      navigate('/purity/calibrations');
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Failed to record calibration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Title level={3} style={{ marginTop: 0 }}>
        <ExperimentOutlined style={{ marginRight: 10 }} />Copper Calibration
      </Title>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={9}>
          <Card title="Reference Weighing">
            <Form
              form={form} layout="vertical" requiredMark="optional"
              onValuesChange={(_, all) => setValues(all)}
            >
              <Form.Item
                name="copperWeightAir" label="Copper Weight in Air (g)"
                rules={[{ required: true, message: 'Enter copper weight in air' }]}
              >
                <InputNumber style={{ width: '100%' }} min={0.0001} step={0.001}
                  precision={4} size="large" autoFocus />
              </Form.Item>
              <Form.Item
                name="copperWeightDiesel" label="Copper Weight in Diesel (g)"
                rules={[{ required: true, message: 'Enter copper weight in diesel' }]}
              >
                <InputNumber style={{ width: '100%' }} min={0.0001} step={0.001}
                  precision={4} size="large" />
              </Form.Item>
              {machines.length > 1 && (
                <Form.Item name="machineId" label="Machine">
                  <Select allowClear placeholder="Default machine"
                    options={machines.map((m) => ({ value: m._id, label: `${m.machineCode} · ${m.name}` }))} />
                </Form.Item>
              )}
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={2} placeholder="Fluid changed, room temperature, …" />
              </Form.Item>
              {canActivate && (
                <Form.Item name="activate" valuePropName="checked">
                  <Checkbox>Activate immediately (becomes the live calibration)</Checkbox>
                </Form.Item>
              )}
              <Button type="primary" size="large" block icon={<SaveOutlined />}
                onClick={onSave} loading={saving} disabled={!preview}>
                Record Calibration
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={15}>
          {preview ? (
            <motion.div
              key={preview.dieselDensity}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card
                title={<span><ThunderboltOutlined style={{ marginRight: 8 }} />Computed Fluid Profile</span>}
                extra={fluid && <Tag color={fluid.color} icon={<FireOutlined />}>{fluid.label}</Tag>}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={12} md={6}>
                    <Statistic title="Diesel Density" value={preview.dieselDensity}
                      precision={5} suffix="g/cm³" valueStyle={{ color: '#722ed1' }} />
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic title="Temperature Index" value={preview.temperatureIndex}
                      precision={3} suffix="%" />
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic title="Weight Difference" value={preview.loss} precision={4} suffix="g" />
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic title="Copper Reference" value={preview.referenceDensity}
                      precision={2} suffix="g/cm³" valueStyle={{ color: '#d4a017' }} />
                  </Col>
                </Row>
                <Alert
                  style={{ marginTop: 16 }} type="info" showIcon
                  message="ρ(diesel) = ρ(copper) × (W(air) − W(diesel)) / W(air)"
                  description="The copper reference has a known density, so its immersion loss reveals the fluid's density. Diesel density doubles as a temperature index — warm diesel is lighter. Saving stores this calibration in the permanent history; only the ACTIVE calibration is used by live purity tests."
                />
              </Card>
            </motion.div>
          ) : (
            <Card style={{ textAlign: 'center', padding: 60 }}>
              <ExperimentOutlined style={{ fontSize: 44, opacity: 0.25 }} />
              <Title level={4} type="secondary" style={{ marginTop: 16 }}>
                Weigh the copper reference
              </Title>
              <Text type="secondary">
                Enter both copper weights to compute the diesel density live.
              </Text>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}
